import classNames from 'classnames';
import { useEffect, useState, useMemo, memo, CSSProperties } from 'react';
import { useLocalizedServiceFactory, useUpsellServices, IService, LocalizedService, RNDService } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../context/Appointment/appointment';
import { useCart, useCartDispatch, Types } from '../../context/cart';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { useHideKioskBackground } from '../../context/Page/page';
import { useBackgroundDataState } from '../../context/backgroundData';
import { CTAButton } from '../../controls/ButtonBase';
import { Heading } from '../../controls/Heading/Heading';
import { Spinner } from '../../controls/Spinner/Spinner';
import { ScrollableBody } from '../Layout/ScrollableBody';
import css from './index.module.scss';
import { getUpdateAptServices } from '../../helpers/Appointment';
import { Display } from './UpsellDisplay/Display';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';

interface ICustomCSSProperties extends CSSProperties {
    /**
     * Passes the number of upsell cards to the scss
     */
    '--upsell-columns'?: number;
}

export interface IProps {
    onProceed: () => void;
    onSkip: () => void;
    /** If only Part B upsell items should display. */
    partB?: boolean;
}

export const Upsell: GoMotoComponent<IProps> = memo((props) => {
    const Localization = useLocalization();
    const serviceFactory = useLocalizedServiceFactory();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const cartDispatch = useCartDispatch();
    const {
        key,
        dealer: {
            localization: { upsellDisclaimer },
        },
    } = useKioskState();
    const cart = useCart();
    const backgroundDataState = useBackgroundDataState();
    useHideKioskBackground()();

    const [upsellServices, setUpsellServices] = useState<ReturnType<typeof serviceFactory>[] | undefined>(undefined);
    const [spgPricedServices, setSpgPricedServices] = useState<IService[]>();

    // RND (already-recommended) op codes are excluded server-side. Wait for them to load before requesting upsells.
    const excludeOpCodes = useMemo<string[]>(() => backgroundDataState.rndServices.map((rndService: RNDService) => rndService.opCode), [backgroundDataState.rndServices]);

    const {
        data: rawUpsellServices,
        loading: upsellLoading,
        error: upsellError,
    } = useUpsellServices(
        key,
        parseInt(appointment.year ?? '0'),
        appointment.make ?? '',
        appointment.model ?? '',
        appointment.mileage ?? -1,
        props.partB,
        excludeOpCodes,
        backgroundDataState.rndServicesInitialized,
    );

    useEffect(() => {
        setSpgPricedServices(backgroundDataState.allServices.filter((service: IService) => !!service.useSpgPricing && !!service.spgPricing));
    }, [backgroundDataState.allServices]);

    useEffect(() => {
        if (upsellError) {
            // error state also has undefined data, so it must be handled before any "not run yet" check
            console.warn('additional services error', upsellError);
            props.onSkip();
        }
        else if (!upsellLoading && rawUpsellServices && rawUpsellServices.length > 0) {
            /*
             * Filtering (RND exclusion, platform image rules) and the 3-item limit are handled server-side;
             * just apply SPG pricing overrides and localize for display.
             */
            setUpsellServices(
                rawUpsellServices
                    .map((raw) => {
                        const spgPricedService = spgPricedServices?.find((spg) => spg.opCode === raw.opCode);
                        if (spgPricedService) {
                            const { priceLow, priceHigh, isSpgOp } = spgPricedService;

                            return {
                                ...raw,
                                priceLow,
                                priceHigh,
                                isSpgOp,
                            };
                        }
                        else {
                            return raw;
                        }
                    })
                    .map(serviceFactory),
            );
        }
        else if (!upsellLoading && rawUpsellServices !== undefined) {
            // request finished and returned no upsells (null/empty) => skip
            props.onSkip();
        }
        // No matching case while still loading or before the request has run (e.g. waiting on RND) => nothing to do
    }, [rawUpsellServices, props, upsellLoading, upsellError, serviceFactory, spgPricedServices]);

    const toggleService: GoMotoFunction<[ReturnType<typeof serviceFactory>], void> = (service) => {
        cartDispatch({
            type: Types.ToggleService,
            payload: service.full,
        });
    };

    const onProceed: VoidFunction = async () => {
        const servicesRecommended = appointment.servicesRecommended || [];
        upsellServices?.forEach((s) => !servicesRecommended.includes(s.id) && servicesRecommended.push(s.id));
        await setAppointment({
            ...appointment,
            servicesRecommended,
            servicesConfirmed: getUpdateAptServices(cart.services),
        });
        props.onProceed();
    };

    return (
        upsellServices === undefined ?
            <Spinner /> :
            <ScrollableBody
                heading={
                    <Heading
                        primaryHeading={<GMAppointmentName />}
                        secondaryHeading={props.partB ? Localization.SelectAccessories : Localization.WeFoundMaintenanceServices}
                        leftAlign
                    />
                }
                proceedButtons={
                    <GMButtonGroup
                        buttons={[
                            (
                                <CTAButton
                                    onClick={onProceed}
                                    styleType={'primary'}
                                    title={Localization.ClickToProceedToNextStep}
                                    label={Localization.Proceed}
                                    id={Localization.ClickToProceedToNextStep}
                                    ariaLabel={Localization.ClickToProceedToNextStep}
                                />
                            ),
                        ]}
                    />
                }
                topDivider
            >
                <>
                    {upsellServices && upsellServices.length ? (
                        <div
                            className={classNames(css.upsellItems, css[upsellServices.length > 1 ? 'multiple' : 'single'])}
                            style={{ '--upsell-columns': upsellServices.length } as ICustomCSSProperties}
                        >
                            {upsellServices.map((upsell: LocalizedService) => {
                                const selected: boolean = cart.services.findIndex((cartService) => cartService.id === upsell.id) > -1;

                                return (
                                    <div key={upsell.id} className={classNames(css.upsellItem, { [css.selected]: selected })}>
                                        <Display
                                            upsell={upsell}
                                            selected={selected}
                                            toggleService={toggleService}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <Spinner />
                    )}
                    {upsellDisclaimer && <div className={css.disclaimer}>{upsellDisclaimer}</div>}
                </>
            </ScrollableBody>
    );
});
